import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { CloudDownload, Loader2 } from 'lucide-react';
import { Button } from '../components/Button';
import { useDb } from '../db/hooks';
import { useUser } from '../auth/session';
import { SyncEngine, type SyncStatus } from './engine';

interface SyncContextValue {
  /** The signed-in account, for its attachment files in storage. */
  userId: string;
  status: SyncStatus;
  syncNow: () => Promise<void>;
}

const SyncContext = createContext<SyncContextValue | null>(null);

/** Sync status and controls, or null in demo mode (no account). */
export function useSync() {
  return useContext(SyncContext);
}

/** Runs sync for the open account database. Renders inside <AccountGate>. */
export function SyncProvider({ children }: { children: React.ReactNode }) {
  const db = useDb();
  const user = useUser();
  const [engine] = useState(() => new SyncEngine(db, user.id));
  const status = useSyncExternalStore(engine.subscribe, engine.getStatus);
  const [skippedRestore, setSkippedRestore] = useState(false);

  useEffect(() => engine.start(), [engine]);

  const value = { userId: user.id, status, syncNow: () => engine.syncNow() };

  // Decide between the app and the restore screen before rendering either, so the app never
  // mounts and then unmounts (which would lose what the user was doing).
  if (!status.initialized) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-clinical-primary" aria-label="Loading" />
      </div>
    );
  }

  if (status.restoring && !skippedRestore) {
    const stuck = status.phase === 'offline' || status.phase === 'error';
    return (
      <div className="flex min-h-dvh items-center justify-center px-6 text-center">
        <div className="max-w-sm space-y-4">
          <CloudDownload className={`mx-auto h-10 w-10 text-clinical-primary ${stuck ? '' : 'animate-pulse'}`} />
          <h1 className="text-xl font-semibold">{stuck ? 'Couldn’t download your data' : 'Getting your data'}</h1>
          <p className="text-sm text-clinical-muted">
            {stuck
              ? status.phase === 'offline'
                ? 'You’re offline. Connect to the internet to bring your RenalFlow data onto this device.'
                : status.error
              : status.restoring.received
                ? `${status.restoring.received} records downloaded…`
                : 'Bringing your RenalFlow data onto this device.'}
          </p>
          {stuck ? (
            <div className="flex justify-center gap-2">
              <Button onClick={() => void engine.syncNow()}>Try again</Button>
              <Button variant="secondary" onClick={() => setSkippedRestore(true)}>
                Continue without it
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}
